import {
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import {
  SetUserContext,
  UserContext,
} from "@redotech/redo-customer-portal-app/contexts/user";
import { getResource } from "@redotech/redo-model/localization/resource";
import type { Order } from "@redotech/redo-model/order";
import { CompleteOrderFulfillmentStatus } from "@redotech/redo-model/shopify-order";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { getDateString } from "@redotech/redo-web/date-utils";
import ChevronDown from "@redotech/redo-web/icon-old/chevron-down.svg";
import ChevronRight from "@redotech/redo-web/icon-old/chevron-right.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { sleep } from "@redotech/util/schedule";
import { memo, MouseEvent, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CurrentItemBeingReturnedContext } from "../contexts/CurrentItemBeingReturnedContext";
import { ItemsReadyForReturnContext } from "../contexts/ItemsReadyForReturnContext";
import { ShippingContext } from "../contexts/ShippingContext";
import {
  fetchOrdersUtil,
  getReturnsFromOrders,
  getReturnsUtil,
} from "../utils/order-utils";
import * as orders from "./orders.module.css";

const styles = {
  tableHeaderText: { color: "#A6A9BA" },
  tableText: { fontFamily: "var(--return-app-body-font-family)" },
};

export const Orders = memo(function Orders() {
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const { setCurrentItemBeingReturned } = useContext(
    CurrentItemBeingReturnedContext,
  );
  const { setItemsReadyForReturn } = useContext(ItemsReadyForReturnContext);
  const { setNewOrderShippingName, setGreenReturnOverride } =
    useContext(ShippingContext);
  const settings = useContext(SettingsContext);
  const screenSize = useScreenType();
  // @ts-expect-error: strict-null-checks
  const copyResource = getResource({ overrides: settings.resourceOverride });

  // If the user has no email or selected order, redirect to login.
  // We won't be able to find any orders for them without an email address.
  useEffect(() => {
    if (user && !user.email) {
      (async () => {
        // @ts-expect-error: strict-null-checks
        setAuth_(undefined);
        // @ts-expect-error: strict-null-checks
        setUser(undefined);
        await sleep(new Temporal.Duration());
        navigate("/returns-portal/login");
      })();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const ordersLoad = useLoad(async () => {
    if (!user) {
      return undefined;
    }
    return await fetchOrdersUtil({ email: user.email });
  }, [user]);

  const returnDataLoad = useLoad(async () => {
    if (!ordersLoad.value) {
      return undefined;
    }
    const returns = getReturnsFromOrders(ordersLoad.value);
    return await getReturnsUtil(returns);
  }, [ordersLoad?.value]);

  const [anchorEl, setAnchorEl] = useState(null);
  // This is used for the view more dropdown. It indicates which order the dropdown menu should contain
  const [openOrderNumber, setOpenOrderNumber] = useState(undefined);
  const renderReturn = (order: Order) => {
    if (!returnDataLoad.value) {
      return null;
    }

    const returnsForOrder = returnDataLoad.value.filter((_return) => {
      return _return.orders.find((data) => data.name === order.shopify.name);
    });

    const copyResource = getResource({
      variant: returnsForOrder[0]?.type || "return",
      // @ts-expect-error: strict-null-checks
      overrides: settings.resourceOverride,
    });

    const handleNavigate = (id: string) => {
      navigate(`/returns-portal/orders/return/${id}`);
    };
    const open = Boolean(anchorEl);
    const handleClick = (e: MouseEvent, orderNumber: any) => {
      setOpenOrderNumber(orderNumber); // @ts-expect-error: strict-null-checks
      setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    if (returnsForOrder.length === 0) {
      //   Show dash
      return "—";
    } else if (returnsForOrder.length === 1) {
      //   Show single item as a link
      return (
        <div
          className={orders.link}
          // @ts-expect-error: strict-null-checks
          onClick={() => handleNavigate(returnsForOrder[0].id)}
        >
          {copyResource.PORTAL_ORDER_TABLE_RETURN_LINK_TEXT}
        </div>
      );
    } else {
      //   Show dropdown
      return (
        <div>
          <div
            className={orders.dropdown}
            onClick={(e) => handleClick(e, order.shopify.name)}
          >
            <div className={orders.link}>View all</div>
            {open && order.shopify.name === openOrderNumber ? (
              <ChevronDown style={{ height: "20px" }} />
            ) : (
              <ChevronRight style={{ height: "20px" }} />
            )}
          </div>
          <Menu
            anchorEl={anchorEl}
            onClose={handleClose}
            open={open && order.shopify.name === openOrderNumber}
          >
            {returnsForOrder.map((_return) => (
              <MenuItem
                key={_return.id}
                onClick={() => handleNavigate(_return.id)}
                style={{ ...styles.tableText, margin: "8px" }}
                value={_return.id}
              >
                {
                  <>
                    {
                      getResource({
                        variant: _return.type || "return",
                        // @ts-expect-error: strict-null-checks
                        overrides: settings.resourceOverride,
                      }).ORDER_DETAILS_LIST_ITEM_TEXT
                    }
                  </>
                }{" "}
                on {getDateString(new Date(_return.createdAt))}
              </MenuItem>
            ))}
          </Menu>
        </div>
      );
    }
  };

  if (!user) {
    return null;
  }

  if (ordersLoad.error || returnDataLoad.error) {
    return (
      <div className={orders.ordersPage}>
        <div className={orders.subheader}>
          There was an error loading your orders.
        </div>
      </div>
    );
  }

  if (
    ordersLoad.pending ||
    !ordersLoad.value ||
    returnDataLoad.pending ||
    !returnDataLoad.value
  ) {
    return <LoadingRedoAnimation />;
  }

  return (
    <div className={orders.ordersPage}>
      <div className={orders.title}>
        <h2>Order History</h2>
      </div>
      {screenSize === ScreenType.MOBILE ? (
        <div>
          {ordersLoad.value.map((order) => (
            <div key={order._id} style={{ marginBottom: "16px" }}>
              <div className={orders.mobileFlex}>
                <div className={orders.subheader}>Order Date</div>
                <div>{getDateString(new Date(order.shopifyCreatedAt))}</div>
              </div>
              <div className={orders.mobileFlex}>
                <div className={orders.subheader}>Order Number</div>
                <div>{order.shopify.name}</div>
              </div>

              <div className={orders.mobileFlex}>
                <div className={orders.subheader}>
                  {copyResource.PORTAL_ORDERS_TABLE_RETURNS_HEADER}
                </div>
                <div>{renderReturn(order)}</div>
              </div>
              <div className={orders.verticalButtonBar}>
                <div
                  // @ts-expect-error: strict-null-checks
                  className={
                    CompleteOrderFulfillmentStatus.fromShopifyOrder(
                      order.shopify,
                    ) !== CompleteOrderFulfillmentStatus.FULFILLED &&
                    CompleteOrderFulfillmentStatus.fromShopifyOrder(
                      order.shopify,
                    ) !== CompleteOrderFulfillmentStatus.PARTIAL &&
                    orders.disabled
                  }
                >
                  <Button
                    disabled={
                      CompleteOrderFulfillmentStatus.fromShopifyOrder(
                        order.shopify,
                      ) !== CompleteOrderFulfillmentStatus.FULFILLED &&
                      CompleteOrderFulfillmentStatus.fromShopifyOrder(
                        order.shopify,
                      ) !== CompleteOrderFulfillmentStatus.PARTIAL
                    }
                    onClick={() => {
                      setCurrentItemBeingReturned(undefined);
                      setItemsReadyForReturn([]);
                      // @ts-expect-error: strict-null-checks
                      setNewOrderShippingName(undefined);
                      setGreenReturnOverride(undefined);
                      navigate(`/returns-portal/orders/${order._id}`);
                    }}
                    size={ButtonSize.SMALL}
                    style={{
                      width: screenSize === ScreenType.MOBILE && "100%",
                    }}
                    theme={ButtonTheme.OUTLINED}
                  >
                    View order
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <TableContainer>
          <Table className={orders["table-text"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={[styles.tableHeaderText, styles.tableText]}>
                  Order Date
                </TableCell>
                <TableCell sx={[styles.tableHeaderText, styles.tableText]}>
                  Order Number
                </TableCell>
                <TableCell sx={[styles.tableHeaderText, styles.tableText]}>
                  {copyResource.PORTAL_ORDERS_TABLE_RETURNS_HEADER}
                </TableCell>
                <TableCell
                  align="center"
                  sx={[styles.tableHeaderText, styles.tableText]}
                >
                  Status
                </TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {ordersLoad.value.map((order, index) => (
                <TableRow key={index}>
                  <TableCell sx={styles.tableText}>
                    {getDateString(new Date(order.shopifyCreatedAt))}
                  </TableCell>
                  <TableCell sx={styles.tableText}>
                    {order.shopify.name}
                  </TableCell>
                  <TableCell sx={styles.tableText}>
                    {renderReturn(order)}
                  </TableCell>
                  <TableCell align="center" sx={styles.tableText}>
                    <Chip
                      color="primary"
                      label={
                        CompleteOrderFulfillmentStatus.fromShopifyOrder(
                          order.shopify as any,
                        )?.displayValue ?? null
                      }
                      style={{
                        ...styles.tableText,

                        backgroundColor: "#555760",
                        color: "#fff",
                        width: "80px",
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={
                      // @ts-expect-error: strict-null-checks
                      settings.orderTracking?.enabled ||
                      CompleteOrderFulfillmentStatus.fromShopifyOrder(
                        order.shopify,
                      ) === CompleteOrderFulfillmentStatus.FULFILLED ||
                      CompleteOrderFulfillmentStatus.fromShopifyOrder(
                        order.shopify,
                      ) === CompleteOrderFulfillmentStatus.PARTIAL
                        ? {}
                        : { opacity: "60%" }
                    }
                  >
                    <div className={orders.buttonBar}>
                      <Button
                        disabled={
                          !(
                            settings?.orderTracking?.enabled ||
                            CompleteOrderFulfillmentStatus.fromShopifyOrder(
                              order.shopify,
                            ) === CompleteOrderFulfillmentStatus.FULFILLED ||
                            CompleteOrderFulfillmentStatus.fromShopifyOrder(
                              order.shopify,
                            ) === CompleteOrderFulfillmentStatus.PARTIAL
                          )
                        }
                        onClick={() => {
                          setCurrentItemBeingReturned(undefined);
                          setItemsReadyForReturn([]);
                          navigate(`/returns-portal/orders/${order._id}`);
                        }}
                        size={ButtonSize.SMALL}
                        style={{ ...styles.tableText, width: "70%" }}
                        theme={ButtonTheme.OUTLINED}
                      >
                        <div>View order</div>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {ordersLoad.value.length === 0 && "No orders found"}
    </div>
  );
});
